import { useState, type ComponentType } from 'react';
import { BulbOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button } from '@acast-tech/decibel';
import { ArrowUpRightFromSquareRegularGlyph } from '@acast-tech/decibel-glyphs';

export type Severity = 'warning' | 'error';

// antd icons type aria-hidden as Booleanish, so the icon slot must accept it too.
type IconComponent = ComponentType<{
  className?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
}>;

export interface Cta {
  text: string;
  href: string;
}

export interface AudienceBannerProps {
  severity: Severity;
  title: string;
  subtitle: string;
  cta?: Cta;
  /** Overrides the severity's default icon when a banner needs its own glyph. */
  icon?: IconComponent;
  /** Used to build the banner/cta/dismiss test ids. */
  testId: string;
  /** localStorage key the dismissed state is persisted under. */
  storageKey: string;
  /**
   * When set, a dismissal is only honoured if it was recorded after this ISO timestamp.
   * Allows re-showing the banner when new expiries occur after the user last dismissed it.
   */
  validAfter?: string;
}

interface SeverityStyle {
  icon: IconComponent;
  gradient: string;
  iconColor: string;
}

// Severity drives icon + accent so presentation stays consistent across banners.
const SEVERITY_STYLES: Record<Severity, SeverityStyle> = {
  warning: {
    icon: BulbOutlined,
    gradient: 'from-[#FFFDF3] to-[#FFF6D0]',
    iconColor: 'text-amber-500',
  },
  error: {
    icon: ExclamationCircleOutlined,
    gradient: 'from-[#FFF5F5] to-[#FFE3E3]',
    iconColor: 'text-red-500',
  },
};

export const AudienceBanner = ({
  severity,
  title,
  subtitle,
  cta,
  icon,
  testId,
  storageKey,
  validAfter,
}: AudienceBannerProps) => {
  const { icon: severityIcon, gradient, iconColor } = SEVERITY_STYLES[severity];
  const Icon = icon ?? severityIcon;
  const [isDismissed, setIsDismissed] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return false;
    if (validAfter) {
      const dismissedAt = Number(stored);
      return dismissedAt >= new Date(validAfter).getTime();
    }
    return Boolean(stored);
  });

  if (isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(storageKey, String(Date.now()));
    setIsDismissed(true);
  };

  return (
    <div
      data-testid={`${testId}-banner`}
      className={`flex w-full flex-row items-start justify-between bg-gradient-to-r ${gradient} p-6 rounded-b-lg`}
    >
      <div className="flex flex-row items-center">
        <Icon className={`text-xl shrink-0 ${iconColor}`} aria-hidden />
        <div className="ml-2">
          <p className="text-sm mb-0 font-medium">{title}</p>
          <p className="text-sm text-[#4F4F4F] mb-0">{subtitle}</p>
        </div>
      </div>
      <div className="flex flex-row items-center gap-4 ml-4 shrink-0 self-center">
        <button
          className="text-sm font-medium whitespace-nowrap cursor-pointer"
          onClick={handleDismiss}
          data-testid={`${testId}-dismiss`}
          aria-label={`Dismiss ${testId} banner`}
        >
          Dismiss
        </button>
        {cta && (
          <Button
            as="a"
            variant="secondary"
            size="small"
            href={cta.href}
            target="_blank"
            rel="noreferrer"
            glyphEnd={ArrowUpRightFromSquareRegularGlyph}
            data-testid={`${testId}-cta`}
          >
            {cta.text}
          </Button>
        )}
      </div>
    </div>
  );
};
